import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./pos-theme.css";
import {
  ScanLine, Receipt, Users, Scissors, Barcode, BarChart3, Settings, LogOut, Sun, Menu, X, Boxes,
} from "lucide-react";

const LINKS = [
  { to: "/pos", label: "Billing", icon: ScanLine, end: true },
  { to: "/pos/bills", label: "Bills", icon: Receipt },
  { to: "/pos/stock", label: "Stock", icon: Boxes },
  { to: "/pos/day", label: "Day Close", icon: Sun },
  { to: "/pos/customers", label: "Customers", icon: Users },
  { to: "/pos/alterations", label: "Alterations", icon: Scissors },
  { to: "/pos/barcodes", label: "Barcodes", icon: Barcode },
  { to: "/pos/reports", label: "Reports", icon: BarChart3 },
  { to: "/pos/settings", label: "Settings", icon: Settings },
];

export default function PosLayout() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) {
      navigate("/auth?redirect=/pos");
    }
  }, [ready, user, navigate]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  if (!user || user.role !== "admin") {
    return (
      <div className="pos-theme min-h-screen grid place-items-center bg-slate-950 text-slate-100">
        <p className="font-mono text-sm">Verifying access…</p>
      </div>
    );
  }

  return (
    <div className="pos-theme min-h-screen bg-slate-950 text-slate-100 md:flex" data-testid="pos-layout">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <img src="/ambrai-logo.png" alt="Amraiya" className="h-10 w-auto" />
          <div className="border-l border-slate-700 pl-2">
            <p className="text-[0.6rem] text-amber-500 uppercase tracking-widest font-semibold leading-none">POS</p>
          </div>
        </div>
        <button onClick={() => setMenuOpen(true)} className="p-2 text-slate-300 hover:text-amber-400" data-testid="pos-menu-open" aria-label="Open menu">
          <Menu size={22} />
        </button>
      </header>

      {/* Drawer overlay (mobile only) */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setMenuOpen(false)} data-testid="pos-menu-overlay" />
      )}

      <aside
        className={`fixed md:sticky top-0 left-0 z-50 md:z-auto h-screen w-64 md:w-56 shrink-0 border-r border-slate-800 flex flex-col bg-slate-900 transform transition-transform duration-200 ${menuOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/ambrai-logo.png" alt="Amraiya" className="h-14 w-auto" />
            <div className="border-l border-slate-700 pl-2 ml-1">
              <p className="text-[0.65rem] text-amber-500 uppercase tracking-widest font-semibold">POS</p>
              <p className="text-[0.55rem] text-slate-500 uppercase tracking-widest">Terminal</p>
            </div>
          </div>
          <button onClick={() => setMenuOpen(false)} className="md:hidden p-1 text-slate-400 hover:text-amber-400" data-testid="pos-menu-close" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-amber-500 text-slate-950 font-medium" : "text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                }`
              }
              data-testid={`pos-nav-${l.label.toLowerCase()}`}
            >
              <l.icon size={16} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800 text-xs text-slate-500 space-y-2">
          <p>Signed in as</p>
          <p className="text-slate-200">{user.name}</p>
          <button
            onClick={() => { logout(); navigate("/auth?redirect=/pos"); }}
            className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors"
            data-testid="pos-logout"
          >
            <LogOut size={13} /> Sign out
          </button>
          <Link to="/" className="block pt-2 border-t border-slate-800 text-slate-500 hover:text-slate-300">
            ← Storefront
          </Link>
        </div>
      </aside>

      <main key={location.pathname} className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
